import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { authApi } from '@/api/auth'
import { usersApi } from '@/api/users'
import { setAccessToken } from '@/api/client'
import type { User, LoginRequest, SignupRequest, GoogleAuthRequest, GoogleAuthResponse } from '@/types/api'
import { toast } from 'sonner'

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (data: LoginRequest) => Promise<void>
  signup: (data: SignupRequest) => Promise<void>
  googleAuth: (data: GoogleAuthRequest) => Promise<GoogleAuthResponse>
  logout: () => Promise<void>
  refetchUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false)
  const queryClient = useQueryClient()

  // Query to get current user
  const {
    data: user,
    isLoading,
    refetch: refetchUser,
    error: userError,
    isFetching,
  } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: async () => {
      try {
        const result = await usersApi.getMe()
        return result
      } catch (error: any) {
        // If we get a 401, it means the refresh token is invalid
        if (error.response?.status === 401) {
          setAccessToken(null)
        }
        throw error
      }
    },
    enabled: isInitialized,
    retry: false,
    staleTime: 0,
    refetchOnMount: true,
  })

  // Initialize auth on mount - try to refresh token
  useEffect(() => {
    let isInitializing = false

    const initAuth = async () => {
      // Prevent multiple simultaneous refresh attempts (React Strict Mode double-render)
      if (isInitializing) {
        return
      }

      isInitializing = true

      try {
        // Add a timeout wrapper to prevent hanging indefinitely
        const refreshPromise = authApi.refresh()
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Refresh timeout')), 2000)
        )

        await Promise.race([refreshPromise, timeoutPromise])
      } catch (error) {
        // Refresh failed, user not logged in
        setAccessToken(null)
      } finally {
        setIsInitialized(true)
      }
    }

    initAuth()
  }, [])

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'me'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Login failed')
    },
  })

  // Signup mutation
  const signupMutation = useMutation({
    mutationFn: authApi.signup,
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Signup failed')
    },
  })

  // Google auth mutation
  const googleAuthMutation = useMutation({
    mutationFn: authApi.googleAuth,
    onSuccess: (data) => {
      // Token is already set in authApi.googleAuth, just mark as initialized
      setIsInitialized(true)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Google authentication failed')
    },
  })

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      queryClient.clear()
      setAccessToken(null)
    },
  })

  const login = async (data: LoginRequest) => {
    await loginMutation.mutateAsync(data)
    // Mark as initialized after successful login
    setIsInitialized(true)
  }

  const signup = async (data: SignupRequest) => {
    await signupMutation.mutateAsync(data)
  }

  const googleAuth = async (data: GoogleAuthRequest) => {
    const result = await googleAuthMutation.mutateAsync(data)
    // Token is already set in API call, onSuccess marks as initialized
    // Query will automatically refetch due to isInitialized changing
    await refetchUser()
    return result
  }

  const logout = async () => {
    await logoutMutation.mutateAsync()
  }

  const value: AuthContextType = {
    user: user || null,
    isLoading: !isInitialized || isLoading,
    isAuthenticated: !!user,
    login,
    signup,
    googleAuth,
    logout,
    refetchUser: async () => {
      await refetchUser()
    },
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
